import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  AlertOctagon, BadgeCheck, Ban, Banknote, BookOpenCheck, Building2, FileSignature, FileText,
  Gauge, Globe, History, KeyRound, Landmark, LayoutDashboard, Loader2, LogOut, Menu, Monitor, ScrollText, Settings,
  ShieldAlert, ShieldCheck, Ship, Snowflake, Truck, UserCheck, Users, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fmtDate, useLang } from "../i18n";
import api, { apiError } from "../api";
import { toast } from "sonner";
import { TrustedDevices } from "./TrustedDevices";

function TenantSwitcher({ user }) {
  const { t } = useLang();
  const { setActingTenant } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  useEffect(() => { api.get("/platform/tenants").then((r) => setTenants(r.data.filter((x) => !["pending", "rejected", "archived"].includes(x.status)))).catch(() => setTenants([])); }, []);
  const current = localStorage.getItem("ec_tenant") || "";
  const change = async (e) => {
    const tid = e.target.value;
    try {
      await setActingTenant(tid);
      toast.success(tid ? t("sw.acting_as") : t("sw.platform_mode"));
      navigate(tid ? "/" : "/plataforma");
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="flex items-center gap-1.5" data-testid="tenant-switcher">
      <Building2 className="w-4 h-4 text-[#044CBC]" />
      <select className="ec-input !w-auto !py-1 text-xs font-semibold max-w-[220px]" value={current} onChange={change} data-testid="tenant-switcher-select" title={t("sw.title")}>
        <option value="">{t("sw.none")}</option>
        {tenants.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
    </div>
  );
}

function LangSwitcher() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white py-0.5 px-1.5" data-testid="language-switcher">
      <Globe className="w-3.5 h-3.5 text-slate-500" />
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`language-switcher-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 text-xs font-bold uppercase rounded-full transition-colors ${
            lang === l ? "bg-[#16335F] text-white" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function AppFooter() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const fmt = new Intl.DateTimeFormat("es-PA", {
    timeZone: "America/Panama", day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  }).format(now);
  const lastLogin = user?.prev_login_at
    ? new Intl.DateTimeFormat(lang === "es" ? "es-PA" : "en-US", {
        timeZone: "America/Panama", day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      }).format(new Date(user.prev_login_at))
    : null;
  return (
    <footer className="border-t border-slate-200 bg-white px-4 sm:px-6 py-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500" data-testid="app-footer">
      <span data-testid="footer-datetime" className="tabular-nums">{fmt} (Panamá)</span>
      {lastLogin && (
        <span data-testid="footer-last-login" className="text-slate-400">
          {t("footer.last_login")}: <span className="font-mono font-semibold text-slate-500">{lastLogin}</span>
        </span>
      )}
      <span data-testid="footer-version" className="font-semibold text-slate-600">{t("auth.version")}</span>
      <img src="/wca.png" alt="World Compliance Association — Organización Asociada" className="h-8 w-auto object-contain" data-testid="footer-wca-logo" />
      <img src="/iso27001-transparent.png" alt="ISO 27001 Bureau Veritas Certification" className="h-8 w-auto object-contain" data-testid="footer-iso-logo" />
      <img src="/latca-transparent.png" alt="LATCA — Latin American Compliance Association" className="h-8 w-auto object-contain" data-testid="footer-latca-logo" />
      <img src="/asocupa-transparent.png" alt="ASOCUPA" className="h-6 w-auto object-contain" data-testid="footer-asocupa-logo" />
      <a href="mailto:helpdesk@kycfederal.com" data-testid="footer-helpdesk-email" className="text-[#044CBC] hover:underline ml-auto">helpdesk@kycfederal.com</a>
    </footer>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [histRows, setHistRows] = useState([]);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return toast.error(t("pw.mismatch"));
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success(t("pw.success"));
      setPwOpen(false);
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  const isSuper = user?.role === "superadmin" || user?.is_super;
  const acting = !!user?.is_super;
  const clientMods = user?.role === "client" ? user?.modules || [] : null;
  const tmods = isSuper ? null : user?.tenant_modules ?? null;
  const isPortal = user?.role === "provider" || (user?.role === "client" && !(clientMods || []).length);
  const superGroup = { label: t("nav.section.platform"), items: [
        { to: "/plataforma", icon: LayoutDashboard, label: t("nav.platform"), testid: "nav-platform" },
        { to: "/plataforma/registros-publicos", icon: ShieldAlert, label: t("nav.public_records"), testid: "nav-public-records" },
        ...(acting ? [] : [{ to: "/busqueda-pep", icon: ShieldAlert, label: t("nav.pep_panama"), testid: "nav-pep-panama" }]),
      ] };
  const ALWAYS_ON = ["vessels", "pep_panama"];
  const alwaysOnItems = [
    { to: "/ofac-vessel", icon: Ship, label: t("nav.vessels"), testid: "nav-vessels", mod: "vessels" },
    { to: "/lista-pep-panama", icon: Landmark, label: t("nav.pep_panama"), testid: "nav-pep-panama", mod: "pep_panama" },
  ];
  const groups = isSuper && !acting
    ? [superGroup]
    : isPortal
    ? [{ label: t("nav.section.main"), items: [{ to: "/portal", icon: LayoutDashboard, label: t("nav.portal"), testid: "nav-portal" }] },
       { label: t("nav.section.screening"), items: alwaysOnItems }]
    : [
    ...(acting ? [superGroup] : []),
    { label: t("nav.section.main"), items: [
      { to: "/", icon: LayoutDashboard, label: t("nav.dashboard"), testid: "nav-dashboard" },
      { to: "/tv", icon: Monitor, label: t("nav.tv"), testid: "nav-tv" },
      ...(clientMods ? [{ to: "/portal", icon: UserCheck, label: t("nav.portal"), testid: "nav-portal" }] : []),
    ] },
    { label: t("nav.section.screening"), items: [
      { to: "/busqueda", icon: ShieldCheck, label: t("nav.sanctions"), testid: "nav-sanctions", mod: "sanctions" },
      { to: "/fbi", icon: ShieldAlert, label: t("nav.fbi"), testid: "nav-fbi", mod: "fbi" },
      { to: "/interpol", icon: ShieldAlert, label: t("nav.interpol"), testid: "nav-interpol", mod: "interpol" },
      { to: "/banco-mundial", icon: Banknote, label: t("nav.wb"), testid: "nav-wb", mod: "wb" },
      { to: "/osint", icon: Globe, label: t("nav.osint"), testid: "nav-osint", mod: "osint" },
      { to: "/namus", icon: Users, label: t("nav.namus"), testid: "nav-namus", mod: "namus" },
      { to: "/icmp", icon: Users, label: t("nav.icmp"), testid: "nav-icmp", mod: "icmp" },
      alwaysOnItems[0],
      { to: "/busqueda-pep", icon: ShieldAlert, label: t("nav.pep"), testid: "nav-pep", mod: "pep" },
      alwaysOnItems[1],
      { to: "/lista-negra", icon: Ban, label: t("nav.blacklist"), testid: "nav-blacklist", mod: "blacklist" },
    ] },
    {
      label: t("nav.section.forms"),
      items: [
        { to: "/formularios/persona-natural", icon: FileText, label: t("nav.dd_natural"), testid: "nav-dd-natural", mod: "dd_natural" },
        { to: "/formularios/persona-juridica", icon: Landmark, label: t("nav.dd_juridica"), testid: "nav-dd-juridica", mod: "dd_juridica" },
        { to: "/formularios/declaracion-fondos", icon: FileSignature, label: t("nav.declaracion"), testid: "nav-declaracion", mod: "declaracion" },
        { to: "/formularios/beneficiario-final", icon: BadgeCheck, label: t("nav.certificacion"), testid: "nav-certificacion", mod: "certificacion" },
        { to: "/formularios/carta-responsabilidad", icon: ScrollText, label: t("nav.carta"), testid: "nav-carta", mod: "carta" },
        { to: "/formularios/dd-pn-aseguradora", icon: FileText, label: t("nav.dd_aseguradora"), testid: "nav-dd-aseguradora", mod: "dd_aseguradora" },
        { to: "/formularios/dd-pj-aseguradora", icon: Landmark, label: t("nav.pj_aseguradora"), testid: "nav-pj-aseguradora", mod: "pj_aseguradora" },
      ],
    },
    {
      label: t("nav.section.compliance"),
      items: [
        { to: "/cumplimiento/riesgo", icon: Gauge, label: t("nav.risk"), testid: "nav-risk", mod: "risk" },
        { to: "/cumplimiento/empleados", icon: UserCheck, label: t("nav.employees"), testid: "nav-employees", mod: "employees" },
        { to: "/cumplimiento/proveedores", icon: Truck, label: t("nav.suppliers"), testid: "nav-suppliers", mod: "suppliers" },
        { to: "/cumplimiento/ros", icon: AlertOctagon, label: t("nav.ros"), testid: "nav-ros", mod: "ros" },
        { to: "/cumplimiento/rte", icon: Banknote, label: t("nav.rte"), testid: "nav-rte", mod: "rte" },
        { to: "/cumplimiento/congelamiento", icon: Snowflake, label: t("nav.freeze"), testid: "nav-freeze", mod: "freeze" },
        { to: "/cumplimiento/manual", icon: BookOpenCheck, label: t("nav.manual"), testid: "nav-manual", mod: "manual" },
      ],
    },
    {
      label: t("nav.section.admin"),
      items: [
        { to: "/historial", icon: History, label: t("nav.history"), testid: "nav-history", mod: "history" },
        ...(["admin", "operator", "auditor"].includes(user?.role) ? [{ to: "/administracion/cyber", icon: ShieldAlert, label: t("nav.cyber"), testid: "nav-cyber", mod: "cyber" },
                                                                    { to: "/administracion/ntas", icon: AlertOctagon, label: t("nav.ntas"), testid: "nav-ntas", mod: "ntas" }] : []),
        ...(user?.role === "admin" ? [{ to: "/usuarios", icon: Users, label: t("nav.users"), testid: "nav-users" },
                                      { to: "/configuracion", icon: Settings, label: t("nav.settings"), testid: "nav-settings" }] : []),
      ],
    },
  ].map((g) => ({ ...g, items: g.items.filter((it) => !it.mod || ALWAYS_ON.includes(it.mod) || ((!clientMods || clientMods.includes(it.mod)) && (tmods == null || tmods.includes(it.mod)))) }))
   .filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="app-layout">
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#16335F] text-white flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-3 px-5 h-16 bg-white border-b border-white/10">
          <img src={user?.tenant_logo || "/logo.png"} alt="KYCFederal" className="h-10 w-auto max-w-[150px] object-contain" data-testid="sidebar-logo" />
          <button className="lg:hidden ml-auto text-slate-500" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">{g.label}</div>
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors ${
                      isActive ? "bg-[#044CBC] text-white font-semibold" : "text-slate-300 hover:bg-white/10 hover:text-white"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-slate-400">
          OFAC · CSNU — {t("dash.lists")}
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="lg:pl-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6" data-testid="topbar">
          <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <img src={user?.tenant_logo || "/logo.png"} alt={user?.tenant_name || "KYCFederal"} className="h-8 w-auto max-w-[130px] object-contain" data-testid="header-tenant-logo" />
          </div>
          <div className="ml-auto flex items-center gap-3">
            {isSuper && <TenantSwitcher user={user} />}
            <LangSwitcher />
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm font-semibold text-slate-800" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-slate-500" data-testid="user-role">
                {{ admin: t("u.admin"), operator: t("u.operator"), auditor: t("u.auditor"), client: t("u.client"), provider: t("u.provider"), superadmin: t("u.superadmin") }[user?.is_super ? "superadmin" : user?.role] || user?.role}
              </div>
            </div>
            <button
              onClick={() => { setHistOpen(true); api.get("/auth/access-history").then((r) => setHistRows(r.data)).catch(() => setHistRows([])); }}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#033A8C] transition-colors"
              data-testid="access-history-button"
              title={t("ah.button")}
            >
              <History className="w-4 h-4" />
              <span className="hidden md:inline">{t("ah.button")}</span>
            </button>
            <button
              onClick={() => setPwOpen(true)}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#033A8C] transition-colors"
              data-testid="change-password-button"
              title={t("sec.title")}
            >
              <KeyRound className="w-4 h-4" />
              <span className="hidden md:inline">{t("sec.title")}</span>
            </button>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 transition-colors"
              data-testid="logout-button"
              title={t("nav.logout")}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">{t("nav.logout")}</span>
            </button>
          </div>
        </header>
        {acting && (
          <div className="sticky top-16 z-20 bg-amber-400 text-[#16335F] text-xs sm:text-sm font-bold px-4 sm:px-6 py-2 flex items-center gap-2 shadow-sm" data-testid="acting-tenant-banner">
            <Building2 className="w-4 h-4 shrink-0" />
            <span>{t("sw.banner")} <span className="underline decoration-2" data-testid="acting-tenant-name">{user?.tenant_name}</span></span>
          </div>
        )}
        {isSuper && !acting && (
          <div className="bg-[#EFF4FC] text-[#16335F] text-xs sm:text-sm px-4 sm:px-6 py-2 flex items-center gap-2 border-b border-[#c9d8f2]" data-testid="select-tenant-hint">
            <Building2 className="w-4 h-4 shrink-0 text-[#044CBC]" />
            <span>{t("sw.hint")}</span>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
        <AppFooter />
      </div>

      {histOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="access-history-modal" onClick={() => setHistOpen(false)}>
          <div className="ec-card p-6 w-full max-w-2xl space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2">
              <History className="w-5 h-5 text-[#044CBC]" /> {t("ah.title")}
            </h2>
            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="access-history-table">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr className="bg-[#16335F] text-white text-left">
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.date")}</th>
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">IP</th>
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.device")}</th>
                  </tr>
                </thead>
                <tbody>
                  {histRows.length === 0 ? (
                    <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-400" data-testid="access-history-empty">{t("ah.empty")}</td></tr>
                  ) : histRows.map((e, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`access-row-${i}`}>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{fmtDate(e.at)}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{e.ip || "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">{e.device || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => setHistOpen(false)} data-testid="access-history-close" className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">
              {t("u.cancel")}
            </button>
          </div>
        </div>
      )}

      {pwOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="change-password-modal">
          <div className="ec-card p-6 w-full max-w-2xl space-y-6 max-h-[90vh] overflow-y-auto">
            <h2 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#044CBC]" /> {t("sec.title")}
            </h2>
            <form onSubmit={submitPw} className="space-y-4">
              <h3 className="font-heading text-base font-extrabold text-slate-900">{t("pw.change")}</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="ec-label">{t("pw.current")}</label>
                  <input className="ec-input" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required data-testid="cp-current-input" />
                </div>
                <div>
                  <label className="ec-label">{t("pw.new")}</label>
                  <input className="ec-input" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required minLength={8} data-testid="cp-new-input" />
                </div>
                <div>
                  <label className="ec-label">{t("pw.confirm")}</label>
                  <input className="ec-input" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required minLength={8} data-testid="cp-confirm-input" />
                </div>
              </div>
              <button type="submit" disabled={pwBusy} data-testid="cp-submit-button" className="px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center justify-center gap-2">
                {pwBusy && <Loader2 className="w-4 h-4 animate-spin" />} {t("pw.change")}
              </button>
            </form>
            <div className="border-t border-slate-200 pt-5">
              <TrustedDevices />
            </div>
            <button type="button" onClick={() => setPwOpen(false)} data-testid="cp-cancel-button" className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">
              {t("u.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
